import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { authApiService } from '../services/authApi';
import { discussionApiService, Discussion, Message, DiscussionDetail } from '../services/discussionApi';
import RatingModal from './RatingModal';

interface MyDiscussionsProps {
  onBack: () => void;
}

const MyDiscussions: React.FC<MyDiscussionsProps> = ({ onBack }) => {
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [selectedDiscussion, setSelectedDiscussion] = useState<Discussion | null>(null);
  const [discussionDetail, setDiscussionDetail] = useState<DiscussionDetail | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [closingDiscussion, setClosingDiscussion] = useState(false);

  useEffect(() => {
    const user = authApiService.getCurrentUser();
    setCurrentUser(user);
    loadDiscussions();
  }, []);

  const loadDiscussions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await discussionApiService.getUserDiscussions();
      setDiscussions(response.discussions);
    } catch (error: any) {
      console.error('Erreur lors du chargement des discussions:', error);
      setError(error.message || 'Erreur lors du chargement des discussions');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (discussionId: string) => {
    setMessagesLoading(true);
    try {
      const response = await discussionApiService.getDiscussionMessages(discussionId);
      setMessages(response.messages);
      setDiscussionDetail(response.discussion);
    } catch (error: any) {
      console.error('Erreur lors du chargement des messages:', error);
      setError(error.message || 'Erreur lors du chargement des messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Aujourd\'hui';
    if (diffDays === 2) return 'Hier';
    if (diffDays <= 7) return `Il y a ${diffDays - 1} jours`;
    
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleCloseDiscussion = () => {
    if (selectedDiscussion && selectedDiscussion.status !== 'closed') {
      setShowRatingModal(true);
    }
  };

  const handleRatingSubmit = async (rating: number, comment: string) => {
    if (!selectedDiscussion) return;
    
    setClosingDiscussion(true);
    try {
      await discussionApiService.closeDiscussion(selectedDiscussion.id, rating, comment);
      
      // Mettre à jour la discussion locale
      const updatedDiscussion = {
        ...selectedDiscussion,
        status: 'closed' as const,
        rating,
        feedbackComment: comment,
        closedAt: new Date().toISOString()
      };
      
      setSelectedDiscussion(updatedDiscussion);
      setShowRatingModal(false);
      
      // Recharger la liste des discussions
      await loadDiscussions();
      
    } catch (error: any) {
      setError(error.message || 'Erreur lors de la fermeture de la discussion');
    } finally {
      setClosingDiscussion(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'En cours';
      case 'completed': return 'Terminée';
      case 'pending': return 'En attente';
      case 'closed': return 'Fermée';
      default: return 'Inconnue';
    }
  };

  if (selectedDiscussion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        {/* Header moderne */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-10">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <motion.button
                  onClick={() => {
                    setSelectedDiscussion(null);
                    setMessages([]);
                    setDiscussionDetail(null);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-3 hover:bg-white/60 rounded-xl transition-all duration-200 group"
                >
                  <svg className="w-5 h-5 text-gray-600 group-hover:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </motion.button>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{selectedDiscussion.title}</h1>
                  <p className="text-sm text-gray-500 flex items-center space-x-2">
                    <span>{messages.length} messages</span>
                    <span>•</span>
                    <span>{formatDate(selectedDiscussion.timestamp)}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-4 py-2 rounded-full text-xs font-semibold ${getStatusColor(selectedDiscussion.status)}`}>
                  {getStatusText(selectedDiscussion.status)}
                </span>
                {selectedDiscussion.status !== 'closed' && (
                  <motion.button
                    onClick={handleCloseDiscussion}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-2 bg-red-500 text-white text-xs font-semibold rounded-full hover:bg-red-600 transition-colors"
                  >
                    Terminer la discussion
                  </motion.button>
                )}
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-lg">🤖</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Messages avec design moderne */}
        <div className="max-w-4xl mx-auto px-6 py-8">
          {messagesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">Chargement des messages...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {messages.map((message, index) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-2xl px-6 py-4 rounded-2xl ${
                    message.role === 'user' 
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg' 
                      : 'bg-white/80 backdrop-blur-sm text-gray-900 shadow-md border border-white/20'
                  }`}>
                    <p className="text-sm leading-relaxed">{message.content}</p>
                    <p className={`text-xs mt-3 ${
                      message.role === 'user' ? 'text-white/70' : 'text-gray-500'
                    }`}>
                      {new Date(message.timestamp).toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Affichage de la notation si discussion fermée */}
        {selectedDiscussion.status === 'closed' && selectedDiscussion.rating && (
          <div className="max-w-4xl mx-auto px-6 py-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-yellow-50 border border-yellow-200 rounded-xl p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Discussion terminée</h3>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm text-gray-600">Votre note :</span>
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={`text-lg ${
                            star <= selectedDiscussion.rating! ? 'text-yellow-400' : 'text-gray-300'
                          }`}
                        >
                          ⭐
                        </span>
                      ))}
                    </div>
                    <span className="text-sm text-gray-600">
                      ({selectedDiscussion.rating}/5)
                    </span>
                  </div>
                  {selectedDiscussion.feedbackComment && (
                    <p className="text-sm text-gray-700 italic">
                      "{selectedDiscussion.feedbackComment}"
                    </p>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  Fermée le {new Date(selectedDiscussion.closedAt!).toLocaleDateString('fr-FR')}
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Modal de notation */}
        <RatingModal
          isOpen={showRatingModal}
          onClose={() => setShowRatingModal(false)}
          onSubmit={handleRatingSubmit}
          discussionTitle={selectedDiscussion.title}
          isLoading={closingDiscussion}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header moderne avec glassmorphism */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <motion.button
                onClick={onBack}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-3 hover:bg-white/60 rounded-xl transition-all duration-200 group"
              >
                <svg className="w-5 h-5 text-gray-600 group-hover:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </motion.button>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Mes discussions avec IRIS
                </h1>
                <p className="text-gray-600 flex items-center space-x-2 mt-1">
                  <span>👋 {currentUser && `${currentUser.firstName} ${currentUser.lastName}`}</span>
                  <span>•</span>
                  <span>{discussions.length} discussion{discussions.length > 1 ? 's' : ''}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                <motion.span 
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="text-white text-xl"
                >
                  🤖
                </motion.span>
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900">IRIS</div>
                <div className="text-xs text-gray-500">Assistant IA</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl mb-6"
          >
            {error}
          </motion.div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 border-4 border-blue-200 border-t-blue-500 rounded-full mb-4"
            />
            <p className="text-gray-600 text-lg">Chargement de vos discussions...</p>
          </div>
        ) : discussions.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20"
          >
            <div className="w-32 h-32 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <span className="text-6xl">💬</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Aucune discussion pour le moment</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
              Commencez votre première conversation avec IRIS pour obtenir des conseils personnalisés sur vos projets digitaux.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
              <motion.button
                onClick={onBack}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 font-semibold shadow-lg"
              >
                ← Retour à l'accueil
              </motion.button>
              <div className="text-sm text-gray-500 flex items-center space-x-2">
                <span>💬</span>
                <span>Utilisez le chatbot flottant pour discuter avec IRIS</span>
              </div>
            </div>
          </motion.div>
        ) : (
          <div className="grid gap-6">
            <AnimatePresence>
              {discussions.map((discussion, index) => (
                <motion.div
                  key={discussion.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300 cursor-pointer group"
                  onClick={() => {
                    setSelectedDiscussion(discussion);
                    loadMessages(discussion.id);
                  }}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                            {discussion.title}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(discussion.status)}`}>
                            {getStatusText(discussion.status)}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                          {discussion.lastMessage}
                        </p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{formatDate(discussion.timestamp)}</span>
                          </div>
                          <span>•</span>
                          <div className="flex items-center space-x-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            <span>{discussion.messageCount} message{discussion.messageCount > 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      </div>
                      <motion.div
                        className="ml-4 p-2 rounded-full bg-gray-100 group-hover:bg-blue-100 transition-colors"
                        whileHover={{ x: 5 }}
                      >
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyDiscussions;
